/**
 * A card's place on a board, as an operation the optimistic layer can hold and roll back.
 *
 * The board is arranged by one field across and, on a swimlaned board, another down, so
 * where a card sits is which buckets those fields put it in. That is what this operation
 * carries: one entry per axis field, naming the buckets it now holds.
 *
 * **A diagonal move and a sideways one are the same operation.** A cell address is a pair,
 * so the optimistic operation always names both axes and a drag that crossed one boundary
 * simply names the axis it did not cross with the key it already had. Nothing downstream
 * asks how many axes changed.
 *
 * An axis absent from {@link BoardPlacement.axes} is left where it is, which is how a
 * confirmed placement works: the write reports only the fields it changed, and the untouched
 * axis is resolved against the board the card is currently drawn on.
 */

import type {CellAddress} from '@github-ui/board-core'
import type {Anchor, ConnectionOperation, SetFieldsOperation} from '@github-ui/optimistic-connection'

import type {ViewRankTarget} from '../../utils/view-rank-target'
import {NO_VALUE_KEY} from './issues/board-payload'
import type {SavedViewsBoardArrangement, SavedViewsBoardMoveResult, SavedViewsBoardRankResult} from './types'

/**
 * The single `setFields` member a board placement travels under.
 *
 * One member rather than one per axis field, so it cannot be mistaken for a registry field
 * identifier by a document that stores the card's own fields under those names.
 */
export const BOARD_PLACEMENT_FIELD = 'boardPlacement'

/** Where a card sits on one axis. */
export type BoardAxisPlacement = {
  /**
   * The buckets this axis's field puts the card in, spelled as the read payload spells
   * them, so the no-value bucket is {@link NO_VALUE_KEY} rather than an empty list.
   *
   * Never empty, and a list from the first release because a multi-valued field puts one
   * card in several buckets on an axis and the browser never learns which kind it is
   * looking at.
   */
  keys: readonly string[]
}

/**
 * Where a card goes inside one cell, named against a neighbor the way a drop names it. A cell key
 * means something only in the arrangement it was read in, so the position carries that too.
 */
export type BoardCellPosition = {cell: CellAddress; anchor: Anchor; arrangement: SavedViewsBoardArrangement}

export type BoardPlacement = {
  /** Keyed by the axis field's registry identifier. An axis absent here is left where it is. */
  axes: Readonly<Record<string, BoardAxisPlacement>>
  /**
   * Where the card goes inside the cell it was dropped in. Absent, or naming a card that is not
   * in the cell, places a card arriving in a cell the way a move does: a ranked card at the top,
   * and an unranked one below the ranked cards.
   */
  position?: BoardCellPosition
  /** Cards this placement marks as ranked in the view, wherever they are drawn. */
  ranked?: readonly string[]
  /**
   * The `viewKey` of the saved view whose order `position` and `ranked` describe. A board of
   * another view applies `axes` alone.
   */
  view?: string
  /**
   * The cells the gesture went between, the rank it writes, and the board it was made on, which
   * the writes and what follows them need and the overlay ignores. `rankStale` marks a drop whose
   * cell could not say where it ranks, so it sends no rank and reloads the cell after the move.
   * `missesAtDrop` is how many moves in the write queue had saved somewhere other than where they
   * were drawn when the drop was made.
   *
   * Absent on a confirmed placement: the server has already written it, and the cell a card
   * came out of is not part of where it now sits.
   */
  gesture?: {
    from: CellAddress
    to: CellAddress
    rank?: ViewRankTarget
    rankStale?: boolean
    board?: GestureBoard
    missesAtDrop?: number
  }
}

/** The board a gesture was made on: its id, and the query key its cards are cached under. */
export type GestureBoard = {id: string; queryKey: readonly unknown[]}

/** Reads a placement off an operation, or `undefined` when it is not one. */
export function readBoardPlacement(operation: ConnectionOperation): BoardPlacement | undefined {
  if (operation.type !== 'setFields') return undefined
  const placement = operation.fields[BOARD_PLACEMENT_FIELD]
  if (placement == null || typeof placement !== 'object') return undefined

  const {axes} = placement as Partial<BoardPlacement>
  if (axes == null || typeof axes !== 'object') return undefined

  return placement as BoardPlacement
}

function placementOperation(itemId: string, placement: BoardPlacement): SetFieldsOperation {
  return {type: 'setFields', id: itemId, fields: {[BOARD_PLACEMENT_FIELD]: placement}}
}

/**
 * What the reader sees the instant they let go: the card in the cell they dropped it in.
 *
 * Both axes are named, whichever the gesture crossed, because the cell the card was aimed at
 * is a pair and the board has to draw the whole of it. A gesture inside one cell changes no
 * field, so it names no axis and moves the card only within the cells it is in.
 *
 * Each axis keeps the buckets the card already held and swaps the one it was dragged out of
 * for the one it was dropped in. A field that takes one value holds one bucket, so that is
 * the same statement as naming the destination; a field that takes several holds the rest of
 * them, and naming the destination alone would drop every value the gesture never touched.
 */
export function toOptimisticPlacementOperation({
  itemId,
  from,
  to,
  arrangement,
  held,
  position,
  ranked,
  view,
  rank,
  rankStale,
  board,
  missesAtDrop,
}: {
  itemId: string
  from: CellAddress
  to: CellAddress
  arrangement: SavedViewsBoardArrangement
  /** Every cell the card is in now. A card on a multi-valued axis is in several. */
  held: readonly CellAddress[]
  position?: BoardCellPosition
  ranked?: readonly string[]
  view?: string
  /** The rank write the gesture sends. */
  rank?: ViewRankTarget
  rankStale?: boolean
  board?: GestureBoard
  missesAtDrop?: number
}): SetFieldsOperation {
  const axes: Record<string, BoardAxisPlacement> = {}
  const isWithinCell = from.columnKey === to.columnKey && from.rowKey === to.rowKey
  if (!isWithinCell) {
    axes[arrangement.columnField] = {
      keys: swapKey(
        held.map(cell => cell.columnKey),
        from.columnKey,
        to.columnKey,
      ),
    }
  }
  if (!isWithinCell && arrangement.rowField != null) {
    axes[arrangement.rowField] = {
      keys: swapKey(
        held.map(cell => cell.rowKey),
        from.rowKey,
        to.rowKey,
      ),
    }
  }

  return placementOperation(itemId, {
    axes,
    ...(position && {position}),
    ...(ranked && {ranked}),
    ...(view !== undefined && {view}),
    gesture: {
      from,
      to,
      ...(rank && {rank}),
      ...(rankStale === true && {rankStale}),
      ...(board && {board}),
      ...(missesAtDrop !== undefined && {missesAtDrop}),
    },
  })
}

/**
 * The buckets one axis holds after the gesture: the ones it held, less the one the card was
 * dragged out of, plus the one it was dropped in.
 *
 * The source key is what says which value to drop, which is the same thing the write sends it
 * for. A card the board is not holding falls back to the destination alone.
 */
function swapKey(current: readonly string[], fromKey: string, toKey: string): string[] {
  const kept = [...new Set(current)].filter(key => key !== fromKey)

  return kept.includes(toKey) ? kept : [...kept, toKey]
}

/**
 * Where the write says the card now sits, which is not where the drag intended to put it: a
 * server that normalised the value, clipped it or left it alone reports what it stored.
 *
 * `undefined` when the write reported no field at all, in which case nothing is folded and
 * the overlay drops onto the base it started from.
 */
export function toConfirmedPlacementOperation(result: SavedViewsBoardMoveResult): SetFieldsOperation | undefined {
  if (result.results.length === 0) return undefined

  return placementOperation(result.itemId, {axes: confirmedAxes(result)})
}

/**
 * Whether a field write leaves the card in `to`. The write reads `cells` back from the record, so
 * it names every cell the card is in, including on an axis the gesture did not cross and someone
 * else changed. A result about another card puts nothing anywhere.
 */
export function movesCardTo(result: SavedViewsBoardMoveResult, {itemId, to}: {itemId: string; to: CellAddress}) {
  return (
    result.itemId === itemId && result.cells.some(cell => cell.columnKey === to.columnKey && cell.rowKey === to.rowKey)
  )
}

function confirmedAxes(result: SavedViewsBoardMoveResult): Record<string, BoardAxisPlacement> {
  const axes: Record<string, BoardAxisPlacement> = {}
  for (const field of result.results) {
    // An empty list is the wire's way of saying the field holds nothing, which is the
    // no-value bucket on a board. The only place the two spellings meet.
    axes[field.field] = {keys: field.keys.length === 0 ? [NO_VALUE_KEY] : field.keys}
  }
  return axes
}

/**
 * What a gesture settles to: the fields its move wrote, and the position and ranks its rank
 * write applied. The rank endpoint writes exactly the placement it was sent or fails, so a rank
 * it confirms is the one the pending placement drew.
 *
 * A result about another card confirms nothing. `undefined` when neither write confirmed
 * anything, in which case nothing is folded and the overlay drops onto the base it started from.
 */
export function toConfirmedGestureOperation(
  operation: SetFieldsOperation,
  {moved, ranked}: {moved?: SavedViewsBoardMoveResult; ranked?: SavedViewsBoardRankResult},
): SetFieldsOperation | undefined {
  const pending = readBoardPlacement(operation)
  const axes = moved?.itemId === operation.id ? confirmedAxes(moved) : {}
  const rankConfirmed = pending !== undefined && ranked?.itemId === operation.id
  if (Object.keys(axes).length === 0 && !rankConfirmed) return undefined

  return placementOperation(operation.id, {
    axes,
    ...(rankConfirmed && pending.position && {position: pending.position}),
    ...(rankConfirmed && pending.ranked && {ranked: pending.ranked}),
    ...(rankConfirmed && pending.view !== undefined && {view: pending.view}),
  })
}
